// Wire-format types and API contract for Questionnaire Design (PRD 05 / 문항 설계).
// Mirrors the disease-code feature's adapter pattern so the mock and HTTP
// implementations are interchangeable via VITE_USE_MOCK_API.
//
// Naming convention notes:
//   - Enum values use SCREAMING_SNAKE to match the canonical wire format used
//     elsewhere in admin-web (Target/Gender/Applicability). The Korean labels
//     stay in the UI layer.
//   - QID format `Q-XXXX` is server-issued. FE never invents one.
//   - The PRO tree (Area > SubArea > Item) is read-only here; it is owned by
//     PRD 04 once that feature ships. For now both features will read from the
//     same shared mock + future `/admin/pro-tree` endpoint (see
//     `pro-tree.types.ts`).

import type { ProTreeNode } from '@/features/pro-tree/types/pro-tree.types';

// ── Enums ─────────────────────────────────────────────────────────────────

export type Target = 'PATIENT' | 'CAREGIVER';
export const TARGETS: readonly Target[] = ['PATIENT', 'CAREGIVER'] as const;

export type Gender = 'COMMON' | 'MALE_ONLY' | 'FEMALE_ONLY';
export const GENDERS: readonly Gender[] = ['COMMON', 'MALE_ONLY', 'FEMALE_ONLY'] as const;

export type Applicability = 'COMMON' | 'DISEASE_GROUP' | 'DISEASE_SPECIFIC';
export const APPLICABILITIES: readonly Applicability[] = [
  'COMMON',
  'DISEASE_GROUP',
  'DISEASE_SPECIFIC',
] as const;

// 8 answer types (PRD 05 §3.3).
export type AnswerType =
  | 'SHORT_ANSWER' // 단답형 (단일선택)
  | 'MULTI_CHOICE' // 중복선택형
  | 'YES_NO' // Yes/No
  | 'OX' // OX (퀴즈형)
  | 'TEXT' // 텍스트 입력형
  | 'SLIDER' // 슬라이더형
  | 'PHOTO' // 사진첨부
  | 'BATTERY'; // 묶음 응답형 (공유 척도)
export const ANSWER_TYPES: readonly AnswerType[] = [
  'SHORT_ANSWER',
  'MULTI_CHOICE',
  'YES_NO',
  'OX',
  'TEXT',
  'SLIDER',
  'PHOTO',
  'BATTERY',
] as const;

// PRD 05 §3.3 — `other` choice is a per-question pre-defined slot whose runtime
// answer is captured as free text (`otherText`). At most one `other` choice
// per question (PRD §8.1).
export type ChoiceType = 'fixed' | 'other';

// Branch ref. `JUMP.targetQid` references another question authored under the
// same (itemId, target) bucket — anti-cycle is enforced server-side at save
// time (PRD §8.2).
export type BranchKind = 'NEXT' | 'END' | 'JUMP';

export interface BranchRef {
  kind: BranchKind;
  targetQid?: string;
}

// ── Per-question config ──────────────────────────────────────────────────

export interface Choice {
  text: string;
  score: number; // integer ≥ 0 (PRD §4.2)
  branch?: BranchRef; // omitted for MULTI_CHOICE (PRD §3.3)
  correct?: boolean; // OX only
  chType?: ChoiceType; // 'other' for 기타 (텍스트입력)
}

export interface TextConfig {
  minChars: number;
  maxChars: number;
  score: number; // single score awarded when input is provided
}

export interface SliderConfig {
  min: number;
  max: number;
  step: number;
  unit: string; // e.g. '점', 'mg/dL'
}

export interface PhotoConfig {
  maxFiles: number;
  maxSizeMB: number;
}

// PRD 05 §3.3 (v1.14) — MULTI_CHOICE 응답 개수 제한. `mode` picks the rule:
//   - 'none'  → 제한 없음 (default; min/max ignored)
//   - 'range' → 범위 제한: respondent must pick between min and max (inclusive)
//   - 'count' → 개수 제한: 1..max (min fixed to 1)
// Admin validation is `min ≥ 1`, `max ≥ min`; actual enforcement/warnings live
// in the patient app PRO evaluation (dependency). NOTE(BE): persist + re-validate
// server-side alongside the choices blob; not yet stored by QuestionService.
export type MultiSelectMode = 'none' | 'range' | 'count';

export interface MultiSelectConfig {
  mode: MultiSelectMode;
  min: number;
  max: number;
}

export interface BatteryItem {
  text: string;
  allowPhoto: boolean;
}

export interface BatteryCategory {
  name: string;
  items: BatteryItem[];
}

export interface BatteryConfig {
  scaleLabels: string[];
  scaleScores: number[];
  categories: BatteryCategory[];
}

// PRD 05 §3.9 scoring metadata. `scoreContribution=false` excludes the question
// from the aggregate R-value sum entirely (semantically distinct from cᵢ=0,
// which keeps the term but zeroes it). `weight`, `reliability`, `min`, `max`
// are preserved when toggled OFF.
export interface ScoringAdvanced {
  scoreContribution: boolean;
  weight: number; // wᵢ — 0.1..10.0, step 0.1, zero not allowed (clarification Q92)
  reliability: number; // cᵢ — 0.0..1.0, step 0.1
  min: number;
  max: number;
}

// ── Question ─────────────────────────────────────────────────────────────

export interface Question {
  qid: string; // 'Q-####'
  itemId: string; // FK to PRO item (PRD 04 tree)
  parentQid: string | null; // null for top-level (2-depth max)
  text: string;
  target: Target;
  gender: Gender;
  applicability: Applicability;
  diseaseGroupId?: number | null; // required when applicability=DISEASE_GROUP
  diseaseCodeId?: number | null; // required when applicability=DISEASE_SPECIFIC
  answerType: AnswerType;
  allowPhoto: boolean;
  choices?: Choice[]; // SHORT_ANSWER | MULTI_CHOICE | YES_NO | OX
  multiConfig?: MultiSelectConfig; // MULTI_CHOICE only
  textConfig?: TextConfig;
  sliderConfig?: SliderConfig;
  photoConfig?: PhotoConfig;
  batteryConfig?: BatteryConfig;
  advanced: ScoringAdvanced;
  // 0-based ordering within (itemId, target, parentQid) bucket. Display number
  // (`N` for top-level, `N-M` for sub) is derived at render time.
  order: number;
  createdAt: string; // ISO-8601 UTC
  updatedAt: string;
  // FEATURE_028 (SHCT-590) — server-side "changed but not yet deployed" marker
  // that drives the yellow 수정 badge on the question card. Optional so a mock or
  // an older cached payload without the field still type-checks; missing = false.
  dirty?: boolean;
}

// ── Inputs ───────────────────────────────────────────────────────────────

export interface CreateQuestionInput {
  itemId: string;
  parentQid?: string | null;
  text: string;
  target: Target;
  gender: Gender;
  applicability: Applicability;
  diseaseGroupId?: number | null;
  diseaseCodeId?: number | null;
  answerType: AnswerType;
  allowPhoto?: boolean;
  choices?: Choice[];
  multiConfig?: MultiSelectConfig;
  textConfig?: TextConfig;
  sliderConfig?: SliderConfig;
  photoConfig?: PhotoConfig;
  batteryConfig?: BatteryConfig;
  advanced: ScoringAdvanced;
}

export type UpdateQuestionInput = Partial<Omit<CreateQuestionInput, 'itemId' | 'parentQid'>>;

// PRD §3.8.1–§3.8.4 — server-side copy.
// `targetItemId` is the destination item (current editor selection).
// `targetColumn` overrides the source's target; other meta is left blank and
// `scoreContribution` is forced to `true` by the server.
export interface CopyQuestionInput {
  targetItemId: string;
  targetColumn: Target;
  // SHCT-862 — PRD 05 v1.34 §3.8.1: sent only from the 메인 문항 카드 entry point, naming the
  // question the copy attaches to. Absent from the 컬럼 헤더 entry point, where the copy is
  // appended at top level.
  //
  // The name must stay `targetParentQid` — it is the wire field `CopyQuestionRequest` reads, and
  // it sits alongside `targetItemId` / `targetColumn` for the same reason. It was briefly sent as
  // `parentQid`, which the server ignored, so every copy landed at top level and the entry point
  // looked implemented while doing nothing.
  targetParentQid?: string;
}

// ── Error envelope ───────────────────────────────────────────────────────

export type QuestionnaireDesignApiErrorCode =
  | 'TEXT_REQUIRED'
  | 'META_REQUIRED'
  | 'DISEASE_GROUP_REQUIRED'
  | 'DISEASE_CODE_REQUIRED'
  | 'ANSWER_TYPE_REQUIRED'
  | 'CHOICE_TEXT_REQUIRED'
  | 'OTHER_CHOICE_LIMIT'
  | 'WEIGHT_OUT_OF_RANGE'
  | 'QUESTION_WEIGHT_OUT_OF_RANGE'
  | 'RELIABILITY_OUT_OF_RANGE'
  | 'RELIABILITY_STEP_INVALID'
  | 'QUESTION_MULTI_CONFIG_INVALID'
  | 'BRANCH_CYCLE'
  | 'BRANCH_TARGET_INVALID'
  | 'QUESTION_REFERENCED_BY_BRANCH'
  | 'MAX_DEPTH_EXCEEDED'
  | 'TEXT_CONFIG_INVALID'
  | 'SLIDER_CONFIG_INVALID'
  | 'PHOTO_CONFIG_INVALID'
  | 'BATTERY_EMPTY'
  | 'NOT_FOUND'
  | 'INSUFFICIENT_PERMISSION'
  | 'UNKNOWN';

export class QuestionnaireDesignApiError extends Error {
  readonly code: QuestionnaireDesignApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: QuestionnaireDesignApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'QuestionnaireDesignApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, QuestionnaireDesignApiError.prototype);
  }
}

// ── Public API surface ───────────────────────────────────────────────────

export interface QuestionnaireDesignApi {
  // PRD §3.1 — read-only PRO tree (Area > SubArea > Item). Owned by PRD 04
  // long-term; routed through this feature now so PRD 05 can ship before PRD 04.
  listProTree(): Promise<ProTreeNode[]>;

  // PRD §3.1 — every question across every item. Powers the tree's disease
  // filter dim/count badges. The BE may eventually expose a leaner
  // `/admin/questionnaire-design/disease-counts?{filter}` aggregation; for
  // now we ship a flat list because the admin scale is tiny (< 200 rows).
  listAllQuestions(): Promise<Question[]>;

  // PRD §3.2 — all questions under an item (flat; sub-questions referenced via
  // `parentQid`).
  listQuestions(itemId: string): Promise<Question[]>;

  createQuestion(input: CreateQuestionInput): Promise<Question>;
  updateQuestion(qid: string, input: UpdateQuestionInput): Promise<Question>;
  // Cascade-deletes sub-questions per PRD §3.7.
  deleteQuestion(qid: string): Promise<void>;

  // PRD §3.8 — server-side copy. Resets branches to NEXT, forces
  // scoreContribution=true, blanks gender/applicability/disease* per §3.8.4.
  copyQuestion(qid: string, input: CopyQuestionInput): Promise<Question>;
}
